"use client";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState, useTransition } from "react";

interface AutocompleteOption {
  label: React.ReactNode;
  value: string | number;
  disabled?: boolean;
}

interface CAutocompleteProps {
  name?: string;
  id?: string;
  label?: React.ReactNode;
  placeholder?: string;
  className?: string;
  value?: string | number;
  onChange?: (value: string | number) => void;
  onFetchOptions: (query: string) => Promise<AutocompleteOption[]>;
  disabled?: boolean;
  required?: boolean;
  errorQuery?: boolean;
  errorMessage?: string;
  minChars?: number;
  debounceMs?: number;
}

const CAutocomplete: React.FC<CAutocompleteProps> = ({
  name,
  id,
  label,
  placeholder = "Type to search...",
  className = "",
  value,
  onChange,
  onFetchOptions,
  disabled = false,
  required = false,
  errorQuery = false,
  errorMessage = "",
  minChars = 1,
  debounceMs = 300,
}) => {
  const inputId = id || name || "cautocomplete";
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isSelectionRef = useRef(false);
  const userInteractedRef = useRef(false); // Track if user has interacted
  const selectedValueRef = useRef<string | number | undefined>(undefined); // Store selected value

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<AutocompleteOption[]>([]);
  const [displayValue, setDisplayValue] = useState("");
  const [highlighted, setHighlighted] = useState(-1);
  const [isPending, startTransition] = useTransition();
  const [isInitialized, setIsInitialized] = useState(false);

  // Reset user interaction when component receives new value (form reset/open)
  useEffect(() => {
    userInteractedRef.current = false;
  }, [value]);

  // Fetch options when displayValue changes
  useEffect(() => {
    // Skip API call if this is from a selection
    if (isSelectionRef.current) {
      isSelectionRef.current = false;
      return;
    }

    if (displayValue.length < minChars) {
      setOptions([]);
      setOpen(false);
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      startTransition(async () => {
        try {
          const results = await onFetchOptions(displayValue);
          setOptions(results);
          // Only auto-open if user has typed (not on mount/init)
          if (results.length > 0 && userInteractedRef.current) {
            setOpen(true);
          }
        } catch (error) {
          console.error("Error fetching autocomplete options:", error);
          setOptions([]);
        }
      });
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [displayValue, minChars, debounceMs, onFetchOptions]);

  // Initialize display value from value prop on mount
  useEffect(() => {
    if (value && !isInitialized) {
      setIsInitialized(true);
      selectedValueRef.current = value;

      // Auto-fetch to get the label for this value (e.g., in edit mode)
      if (disabled) {
        // Fetch with empty query to get all options or use the value as query
        startTransition(async () => {
          try {
            const results = await onFetchOptions("");
            setOptions(results);
            // Find and set the display value
            const selectedOption = results.find((opt) => opt.value === value);
            if (selectedOption && typeof selectedOption.label === "string") {
              isSelectionRef.current = true; // Prevent fetch effect from triggering
              setDisplayValue(selectedOption.label);
            }
          } catch (error) {
            console.error(
              "Error fetching initial autocomplete options:",
              error,
            );
          }
        });
      }
    }
  }, [value, isInitialized, disabled, onFetchOptions]);

  // Update display value when value prop changes externally or when options load
  useEffect(() => {
    // Skip if user is actively typing (selectedValueRef cleared but value prop still set)
    // This prevents the effect from overriding user's typing/clearing actions
    if (selectedValueRef.current === undefined && value) {
      return; // User cleared and is typing, don't interfere
    }

    // If we have a selected value but no display, try to find the label from options
    if (
      value === selectedValueRef.current &&
      !displayValue &&
      options.length > 0
    ) {
      const selectedOption = options.find((opt) => opt.value === value);
      if (selectedOption && typeof selectedOption.label === "string") {
        setDisplayValue(selectedOption.label);
      }
      return;
    }

    // If value matches what we have selected and we have display, don't override
    if (value === selectedValueRef.current && displayValue) {
      return;
    }

    // If value changed externally (like form reset), update
    if (value && options.length > 0) {
      const selectedOption = options.find((opt) => opt.value === value);
      if (selectedOption && typeof selectedOption.label === "string") {
        isSelectionRef.current = true; // Prevent API call
        selectedValueRef.current = value;
        setDisplayValue(selectedOption.label);
      }
    } else if (!value && selectedValueRef.current !== undefined) {
      // Value was cleared (form reset or user cleared)
      selectedValueRef.current = undefined;
      setDisplayValue("");
    }
  }, [value, options, displayValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setHighlighted(-1);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      if (options.length > 0) {
        setOpen(true);
      }
      return;
    }

    if (open) {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlighted((prev) =>
            prev < options.length - 1 ? prev + 1 : prev,
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlighted((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case "Enter":
          e.preventDefault();
          if (highlighted >= 0 && options[highlighted]) {
            handleSelect(options[highlighted]);
          }
          break;
        case "Escape":
          setOpen(false);
          setHighlighted(-1);
          break;
      }
    }
  };

  const handleSelect = (option: AutocompleteOption) => {
    const labelText =
      typeof option.label === "string" ? option.label : String(option.value);

    isSelectionRef.current = true; // Flag to prevent API call
    userInteractedRef.current = false; // Reset interaction to prevent auto-open
    selectedValueRef.current = option.value; // Store the selected value
    setDisplayValue(labelText);
    setOpen(false);
    setHighlighted(-1);
    onChange?.(option.value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    userInteractedRef.current = true; // User is typing
    selectedValueRef.current = undefined; // Clear selected value when typing
    setDisplayValue(newValue);
    setHighlighted(-1);

    // If user cleared the input, notify parent
    if (newValue === "" && onChange) {
      onChange("");
    }
  };

  const handleInputFocus = () => {
    // Don't auto-open on focus - only open when user types or clicks with results
  };

  const handleInputClick = () => {
    // Only open if user has typed and there are results
    if (displayValue.length >= minChars && options.length > 0 && !disabled) {
      userInteractedRef.current = true;
      setOpen(true);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label
          htmlFor={inputId}
          className="text-sm text-muted-foreground mb-1 block"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          name={name}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={`
            w-full border rounded px-3 py-2 outline-none placeholder:italic placeholder:text-sm
            focus:ring-2 focus:border-transparent
            ${
              errorQuery
                ? "border-red-500 focus:ring-red-500"
                : "focus:ring-primary"
            }
            ${disabled ? "opacity-50 cursor-not-allowed" : "dark:bg-black/10"}
            ${isPending ? "pr-10" : ""}
          `}
          autoComplete="off"
        />
        {isPending && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
          </div>
        )}
      </div>
      {errorQuery && errorMessage && (
        <p className="text-red-500 text-xs mt-1">{errorMessage}</p>
      )}

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 right-0 mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
          >
            {isPending ? (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <div className="w-4 h-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Loading...
              </div>
            ) : options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                {displayValue.length < minChars
                  ? `Type at least ${minChars} character${
                      minChars > 1 ? "s" : ""
                    } to search`
                  : "No results found"}
              </div>
            ) : (
              <ul className="py-1">
                {options.map((option, index) => (
                  <li
                    key={`${option.value}-${index}`}
                    className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                      option.disabled
                        ? "opacity-50 cursor-not-allowed"
                        : highlighted === index
                          ? "bg-primary/10 text-primary"
                          : "text-foreground hover:bg-accent hover:text-accent-foreground"
                    }`}
                    onClick={() => !option.disabled && handleSelect(option)}
                    onMouseEnter={() =>
                      !option.disabled && setHighlighted(index)
                    }
                  >
                    {option.label}
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CAutocomplete;
